/**
 * i18n bootstrap.  Three languages supported:
 *   en · English   (default)
 *   hi · हिन्दी    (Hindi)
 *   bn · বাংলা    (Bengali)
 *
 * We ship a minimal, hand-crafted dictionary for the top-of-funnel surfaces
 * (nav, homepage hero, footer CTA, headings, key CTAs).  Deeper content
 * pages remain in English until we translate them piece-by-piece.  All
 * strings are namespaced by feature so we can extend without collisions.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const en = {
  translation: {
    nav: {
      apps: "Apps",
      journey: "Journey",
      books: "Books",
      pricing: "Pricing",
      lessons: "Lessons",
      dashboard: "Dashboard",
      login: "Sign in",
      signup: "Start free",
      logout: "Sign out",
    },
    hero: {
      eyebrow: "One subscription · every app · one road trip",
      title1: "Prompt an app.",
      title2: "Explore a lesson.",
      title3: "Read a pop-up.",
      body: "Durga is the only place you can prompt a real Apple app, ship a website, document any codebase, and package a Linux release — all under one warm, teacher-led adventure across North India.",
      cta_start: "Start the adventure",
      cta_map: "Walk the map",
      free: "free to begin →",
      trust_no_card: "No credit card to start",
      trust_platforms: "Apple + Linux + Web",
      trust_teacher: "Ms. Pradhan, always on call",
    },
    footer_cta: {
      eyebrow: "Place · Begin here",
      title: "Begin the",
      title_em: "adventure",
      body: "Free to start. No credit card. Ms. Pradhan is waiting at the first checkpoint.",
      cta_signup: "Create your account",
      cta_map: "Preview the map",
    },
    common: {
      loading: "Loading…",
      close: "Close",
      cancel: "Cancel",
      save: "Save",
      language: "Language",
    },
    pradhan: {
      voice_play: "Listen in Ms. Pradhan's voice",
      voice_stop: "Stop",
      chat_placeholder: "Ask Ms. Pradhan…",
      chat_empty: "Ready when you are, dear.",
    },
    testimonials: {
      title: "A workshop that ships every week.",
      submit: "Share your story",
      submit_body: "Tell us what you shipped and how it felt.",
      submit_placeholder: "In 2–3 sentences: what did you build?",
      submit_role: "Your role (e.g. Parent · Bengaluru)",
      submit_project: "Project name (optional)",
      submit_send: "Send for review",
      submit_video: "Record a 30-sec video",
      submit_video_stop: "Stop recording",
      submit_video_pick: "Or upload a file",
      submit_video_ready: "Video ready",
      submit_video_clear: "Remove video",
      pending: "Thanks! Your story is waiting for review.",
    },
    physical: {
      eyebrow: "Physical evidence · Tangible magic",
      title1: "Hold your progress in",
      title2: "your hands",
      title3: ".",
      body: "Every big idea gets a paper edition. Hardcover pop-up books for the family coffee-table, printable teacher packs for the classroom, and a real .deb file you can copy onto a USB stick. This is a platform you can touch.",
      cta_books: "Browse the pop-up books",
      cta_school: "School kits",
    },
    price: {
      eyebrow: "Price · One account · every app",
      title1: "Everything you need,",
      title2: "in one place",
      title3: ".",
      body: "Five apps · a road-trip curriculum · pop-up books · Ms. Pradhan on tap — all under a single monthly subscription.",
    },
    certificate: {
      title: "Checkpoint cleared!",
      subtitle: "You've earned a printable certificate.",
      body: "Ms. Pradhan is proud of you.  Download your keepsake below.",
      download: "Download certificate",
      later: "Later",
    },
    mendel: {
      eyebrow: "Mendel · Biological Programming Workbench",
      title1: "Program the",
      title2: "language of life",
      title3: ".",
      body: "Explore genomes.  Understand genes.  Model biological systems.  Simulate inheritance, molecular interactions, and cellular behaviour.  Mendel is an interactive environment for computational biology — with Ms. Pradhan riding shotgun.",
      cta_ide: "Open the Genome IDE",
      cta_lessons: "Begin with Ms. Pradhan",
    },
  },
};

const hi = {
  translation: {
    nav: {
      apps: "ऐप्स",
      journey: "यात्रा",
      books: "किताबें",
      pricing: "कीमत",
      lessons: "पाठ",
      dashboard: "डैशबोर्ड",
      login: "साइन इन",
      signup: "मुफ्त शुरू करें",
      logout: "साइन आउट",
    },
    hero: {
      eyebrow: "एक सदस्यता · हर ऐप · एक सफर",
      title1: "एक ऐप को प्रोम्प्ट करें।",
      title2: "एक पाठ खोजें।",
      title3: "एक पॉप-अप पढ़ें।",
      body: "दुर्गा एकमात्र जगह है जहाँ आप एक असली Apple ऐप बना सकते हैं, एक वेबसाइट भेज सकते हैं, किसी भी codebase का दस्तावेज़ीकरण कर सकते हैं, और एक Linux रिलीज़ पैकेज कर सकते हैं — यह सब उत्तर भारत की एक गर्मजोश, शिक्षक-नेतृत्व वाली यात्रा के अंतर्गत।",
      cta_start: "यात्रा शुरू करें",
      cta_map: "नक्शा देखें",
      free: "शुरुआत मुफ्त →",
      trust_no_card: "क्रेडिट कार्ड की जरूरत नहीं",
      trust_platforms: "Apple + Linux + Web",
      trust_teacher: "प्रधान जी हमेशा साथ",
    },
    footer_cta: {
      eyebrow: "स्थान · यहाँ से शुरू करें",
      title: "यात्रा",
      title_em: "शुरू करें",
      body: "मुफ्त शुरुआत। क्रेडिट कार्ड की जरूरत नहीं। प्रधान जी पहले पड़ाव पर आपका इंतज़ार कर रही हैं।",
      cta_signup: "अपना खाता बनाएँ",
      cta_map: "नक्शा देखें",
    },
    common: {
      loading: "लोड हो रहा है…",
      close: "बंद",
      cancel: "रद्द",
      save: "सहेजें",
      language: "भाषा",
    },
    pradhan: {
      voice_play: "प्रधान जी की आवाज़ में सुनें",
      voice_stop: "रोकें",
      chat_placeholder: "प्रधान जी से पूछें…",
      chat_empty: "जब भी तैयार हों, बच्चे।",
    },
    testimonials: {
      title: "एक कार्यशाला जो हर सप्ताह भेजती है।",
      submit: "अपनी कहानी साझा करें",
      submit_body: "बताइए आपने क्या बनाया और कैसा लगा।",
      submit_placeholder: "2–3 वाक्यों में: आपने क्या बनाया?",
      submit_role: "आपकी भूमिका (जैसे अभिभावक · बेंगलुरु)",
      submit_project: "प्रोजेक्ट का नाम (वैकल्पिक)",
      submit_send: "समीक्षा के लिए भेजें",
      submit_video: "30-सेकंड का वीडियो रिकॉर्ड करें",
      submit_video_stop: "रिकॉर्डिंग रोकें",
      submit_video_pick: "या फ़ाइल अपलोड करें",
      submit_video_ready: "वीडियो तैयार",
      submit_video_clear: "वीडियो हटाएँ",
      pending: "धन्यवाद! आपकी कहानी समीक्षा की प्रतीक्षा कर रही है।",
    },
    physical: {
      eyebrow: "भौतिक प्रमाण · मूर्त जादू",
      title1: "अपनी प्रगति",
      title2: "अपने हाथों में",
      title3: " रखें।",
      body: "हर बड़ा विचार एक पेपर संस्करण पाता है। परिवार की कॉफी-टेबल के लिए हार्डकवर पॉप-अप किताबें, कक्षा के लिए प्रिंट करने योग्य शिक्षक पैक, और एक असली .deb फ़ाइल जिसे आप USB स्टिक पर कॉपी कर सकते हैं। यह एक ऐसा प्लेटफ़ॉर्म है जिसे आप छू सकते हैं।",
      cta_books: "पॉप-अप किताबें देखें",
      cta_school: "स्कूल किट",
    },
    price: {
      eyebrow: "कीमत · एक खाता · हर ऐप",
      title1: "जो कुछ आपको चाहिए,",
      title2: "एक ही जगह",
      title3: "।",
      body: "पाँच ऐप्स · एक रोड-ट्रिप पाठ्यक्रम · पॉप-अप किताबें · जब चाहें प्रधान जी — सब कुछ एक ही मासिक सदस्यता में।",
    },
    certificate: {
      title: "पड़ाव पार हो गया!",
      subtitle: "आपने एक प्रिंट करने योग्य प्रमाणपत्र अर्जित किया है।",
      body: "प्रधान जी आप पर गर्व करती हैं। नीचे अपना यादगार प्रमाणपत्र डाउनलोड करें।",
      download: "प्रमाणपत्र डाउनलोड करें",
      later: "बाद में",
    },
    mendel: {
      eyebrow: "मेंडेल · जैविक प्रोग्रामिंग वर्कबेंच",
      title1: "जीवन की",
      title2: "भाषा को प्रोग्राम करें",
      title3: "।",
      body: "जीनोम की खोज करें। जीन को समझें। जैविक प्रणालियों का मॉडल बनाएँ। वंशानुक्रम, आणविक पारस्परिक क्रियाएँ और कोशिकीय व्यवहार का अनुकरण करें। मेंडेल कम्प्यूटेशनल जीवविज्ञान के लिए एक इंटरैक्टिव वातावरण है — प्रधान जी आपके साथ हैं।",
      cta_ide: "जीनोम IDE खोलें",
      cta_lessons: "प्रधान जी के साथ शुरू करें",
    },
  },
};

const bn = {
  translation: {
    nav: {
      apps: "অ্যাপস",
      journey: "যাত্রা",
      books: "বই",
      pricing: "মূল্য",
      lessons: "পাঠ",
      dashboard: "ড্যাশবোর্ড",
      login: "সাইন ইন",
      signup: "বিনামূল্যে শুরু করুন",
      logout: "সাইন আউট",
    },
    hero: {
      eyebrow: "একটি সাবস্ক্রিপশন · প্রতিটি অ্যাপ · একটি রোড-ট্রিপ",
      title1: "একটি অ্যাপ প্রম্পট করুন।",
      title2: "একটি পাঠ অন্বেষণ করুন।",
      title3: "একটি পপ-আপ পড়ুন।",
      body: "দুর্গা হল একমাত্র জায়গা যেখানে আপনি একটি বাস্তব Apple অ্যাপ প্রম্পট করতে পারেন, একটি ওয়েবসাইট পাঠাতে পারেন, যেকোনো কোডবেস নথিভুক্ত করতে পারেন, এবং একটি Linux রিলিজ প্যাকেজ করতে পারেন — সবকিছুই উত্তর ভারতের একটি উষ্ণ, শিক্ষক-পরিচালিত অভিযানের অধীনে।",
      cta_start: "অ্যাডভেঞ্চার শুরু করুন",
      cta_map: "মানচিত্র দেখুন",
      free: "শুরু বিনামূল্যে →",
      trust_no_card: "ক্রেডিট কার্ডের প্রয়োজন নেই",
      trust_platforms: "Apple + Linux + Web",
      trust_teacher: "মিসেস প্রধান, সর্বদা প্রস্তুত",
    },
    footer_cta: {
      eyebrow: "স্থান · এখান থেকে শুরু করুন",
      title: "অভিযান",
      title_em: "শুরু করুন",
      body: "বিনামূল্যে শুরু। ক্রেডিট কার্ডের প্রয়োজন নেই। মিসেস প্রধান প্রথম চেকপয়েন্টে অপেক্ষা করছেন।",
      cta_signup: "আপনার অ্যাকাউন্ট তৈরি করুন",
      cta_map: "মানচিত্র দেখুন",
    },
    common: {
      loading: "লোড হচ্ছে…",
      close: "বন্ধ",
      cancel: "বাতিল",
      save: "সংরক্ষণ",
      language: "ভাষা",
    },
    pradhan: {
      voice_play: "মিসেস প্রধানের কণ্ঠে শুনুন",
      voice_stop: "থামুন",
      chat_placeholder: "মিসেস প্রধানকে জিজ্ঞাসা করুন…",
      chat_empty: "যখন আপনি প্রস্তুত, প্রিয়।",
    },
    testimonials: {
      title: "একটি কর্মশালা যা প্রতি সপ্তাহে পাঠায়।",
      submit: "আপনার গল্প শেয়ার করুন",
      submit_body: "আমাদের বলুন আপনি কী তৈরি করেছেন এবং কেমন লেগেছে।",
      submit_placeholder: "২–৩ বাক্যে: আপনি কী তৈরি করেছেন?",
      submit_role: "আপনার ভূমিকা (যেমন অভিভাবক · কলকাতা)",
      submit_project: "প্রকল্পের নাম (ঐচ্ছিক)",
      submit_send: "পর্যালোচনার জন্য পাঠান",
      submit_video: "৩০ সেকেন্ডের ভিডিও রেকর্ড করুন",
      submit_video_stop: "রেকর্ডিং বন্ধ করুন",
      submit_video_pick: "অথবা একটি ফাইল আপলোড করুন",
      submit_video_ready: "ভিডিও প্রস্তুত",
      submit_video_clear: "ভিডিও সরান",
      pending: "ধন্যবাদ! আপনার গল্প পর্যালোচনার অপেক্ষায় আছে।",
    },
    physical: {
      eyebrow: "শারীরিক প্রমাণ · স্পর্শযোগ্য জাদু",
      title1: "আপনার অগ্রগতি",
      title2: "আপনার হাতে",
      title3: " ধরুন।",
      body: "প্রতিটি বড় ধারণা একটি কাগজের সংস্করণ পায়। পরিবারের কফি-টেবিলের জন্য হার্ডকভার পপ-আপ বই, ক্লাসরুমের জন্য প্রিন্টযোগ্য শিক্ষক প্যাক, এবং একটি বাস্তব .deb ফাইল যা আপনি USB স্টিকে কপি করতে পারেন। এটি এমন একটি প্ল্যাটফর্ম যা আপনি স্পর্শ করতে পারেন।",
      cta_books: "পপ-আপ বই দেখুন",
      cta_school: "স্কুল কিট",
    },
    price: {
      eyebrow: "মূল্য · একটি অ্যাকাউন্ট · প্রতিটি অ্যাপ",
      title1: "আপনার যা কিছু দরকার,",
      title2: "একই জায়গায়",
      title3: "।",
      body: "পাঁচটি অ্যাপ · একটি রোড-ট্রিপ পাঠ্যক্রম · পপ-আপ বই · যখন চান মিসেস প্রধান — সবকিছু একটি মাত্র মাসিক সাবস্ক্রিপশনে।",
    },
    certificate: {
      title: "চেকপয়েন্ট পার!",
      subtitle: "আপনি একটি প্রিন্টযোগ্য শংসাপত্র অর্জন করেছেন।",
      body: "মিসেস প্রধান আপনার জন্য গর্বিত। নীচে আপনার স্মারক ডাউনলোড করুন।",
      download: "শংসাপত্র ডাউনলোড করুন",
      later: "পরে",
    },
    mendel: {
      eyebrow: "মেন্ডেল · জৈবিক প্রোগ্রামিং ওয়ার্কবেঞ্চ",
      title1: "জীবনের",
      title2: "ভাষা প্রোগ্রাম করুন",
      title3: "।",
      body: "জিনোম অন্বেষণ করুন। জিন বুঝুন। জৈবিক সিস্টেম মডেল করুন। উত্তরাধিকার, আণবিক মিথস্ক্রিয়া এবং কোষীয় আচরণ অনুকরণ করুন। মেন্ডেল হল গণনামূলক জীববিজ্ঞানের জন্য একটি ইন্টারেক্টিভ পরিবেশ — মিসেস প্রধান আপনার সাথে আছেন।",
      cta_ide: "জিনোম IDE খুলুন",
      cta_lessons: "মিসেস প্রধানের সাথে শুরু করুন",
    },
  },
};

const STORAGE_KEY = "dpa_lang";
const initialLng =
  (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) ||
  (typeof navigator !== "undefined" && (navigator.language || "").slice(0, 2)) ||
  "en";

i18n.use(initReactI18next).init({
  resources: { en, hi, bn },
  lng: ["en", "hi", "bn"].includes(initialLng) ? initialLng : "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

if (typeof window !== "undefined") {
  i18n.on("languageChanged", (lng) => {
    try { localStorage.setItem(STORAGE_KEY, lng); } catch { /* ignore */ }
    document.documentElement.setAttribute("lang", lng);
  });
  document.documentElement.setAttribute("lang", i18n.language);
}

export const SUPPORTED_LANGS = [
  { code: "en", label: "English",  short: "EN" },
  { code: "hi", label: "हिन्दी",   short: "हिं" },
  { code: "bn", label: "বাংলা",   short: "বাং" },
];

export default i18n;
